import type { z } from 'zod';

import {
  genUiT,
  resolveGenUiLocale,
  type HotelPolicyLimitsSchema,
  HOTEL_POLICY_RULE_KEYS,
} from '@voll-intelligence/genui-contract';

type PolicyLimits = z.infer<typeof HotelPolicyLimitsSchema>;
type PolicyRuleKey = (typeof HOTEL_POLICY_RULE_KEYS)[number];

/** O limite que dá número ao motivo de cada regra; regra sem entrada aqui só
 *  tem a frase sem número. */
const RULE_LIMIT = {
  bookingWindow: (l) => l.bookingWindowDays,
  bookingWindowInternational: (l) => l.bookingWindowInternationalDays,
  maxPrice: (l) => l.maxNightly,
  maxPriceInternational: (l) => l.maxNightlyInternational,
  maxPricePerCity: (l) => l.maxNightlyCity,
} satisfies Partial<Record<PolicyRuleKey, (limits: PolicyLimits) => number | undefined>>;

type LimitedRuleKey = keyof typeof RULE_LIMIT;

const hasLimitVariant = (key: PolicyRuleKey): key is LimitedRuleKey => key in RULE_LIMIT;

const isRuleKey = (key: string): key is PolicyRuleKey =>
  (HOTEL_POLICY_RULE_KEYS as readonly string[]).includes(key);

/** Número SEM moeda: `policyLimits` não a informa, e inventar "R$" seria
 *  mentir sobre dinheiro. */
function formatLimit(value: number, locale: string): string {
  return new Intl.NumberFormat(resolveGenUiLocale(locale), { maximumFractionDigits: 2 }).format(
    value,
  );
}

/**
 * O motivo de cada regra quebrada, traduzido, na ORDEM recebida (a canônica do
 * projetor — quem renderiza não reordena). Chave desconhecida some em
 * silêncio: um rótulo genérico seria pior que a ausência dele.
 */
export function hotelPolicyReasons(
  violations: readonly string[] | undefined,
  limits: PolicyLimits | undefined,
  locale: string,
): string[] {
  return (violations ?? []).filter(isRuleKey).map((key) => {
    const limit = limits && hasLimitVariant(key) ? RULE_LIMIT[key](limits) : undefined;
    return hasLimitVariant(key) && limit !== undefined
      ? genUiT(locale, `hotel.policyRule.${key}WithLimit`, { value: formatLimit(limit, locale) })
      : genUiT(locale, `hotel.policyRule.${key}`);
  });
}

/** A régua da lista de quartos: o teto da cidade quando existe (o mais
 *  específico), senão o geral. Sem nenhum dos dois, nada a mostrar. */
export function hotelPolicyCeiling(
  limits: PolicyLimits | undefined,
  locale: string,
): { label: string; hint: string } | undefined {
  const ceiling = limits?.maxNightlyCity ?? limits?.maxNightly;
  if (ceiling === undefined) return undefined;
  return {
    label: genUiT(locale, 'hotelRooms.policyCeilingAbove', { value: formatLimit(ceiling, locale) }),
    hint: genUiT(locale, 'hotelRooms.policyCeilingHint'),
  };
}
